import React, { useEffect, useState } from 'react';
import { Input, Table, message, Select, Checkbox, Button } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import type { ColumnsType } from 'antd/es/table';
import { PageTitle, StyledCard } from '../common';
import { theme } from '../../theme';

const { Option } = Select;

// Interface para el padrón
interface Cliente {
  id: number;
  denominacion: string;
  nc: string;
}

// Interface para tickets pendientes
interface TicketPendiente {
  id: number;
  numero_ticket: number;
  numero_remito: string;
  dtv: string;
  kilos_netos: number;
  fecha: string;
  cliente: number;
  precio_por_tonelada?: number;
  subtotal?: number;
}

// Interface para retenciones
interface Retencion {
  id: number;
  descripcion: string;
  tipo: string;
  porcentaje: number;
  monto_fijo: number;
  portonelada: number;
  toneladas: number;
  provincia: number;
  provincia_nombre: string;
  empresa: number;
  empresa_nombre: string;
  alta: number;
  aplicar: string;
  acumulado_ganancia: number;
  seleccionada?: boolean;
}

const LiquidacionCompra: React.FC = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [searchCliente, setSearchCliente] = useState('');
  const [showSearchCliente, setShowSearchCliente] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [ticketsPendientes, setTicketsPendientes] = useState<TicketPendiente[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ivaPorcentaje, setIvaPorcentaje] = useState<string>('10.5');
  const [retencionesActivas, setRetencionesActivas] = useState<boolean>(true);
  const [retenciones, setRetenciones] = useState<Retencion[]>([]);
  const [loadingRetenciones, setLoadingRetenciones] = useState(false);
  const [loadingGrabar, setLoadingGrabar] = useState(false);
  const [acumuladoGanancias, setAcumuladoGanancias] = useState<number>(0);
  const [loadingAcumulado, setLoadingAcumulado] = useState(false);
  const { getAccessTokenSilently, user } = useAuth0();

  // Cargar clientes al inicializar
  useEffect(() => {
    if (user?.id_empresa) {
      fetchClientes();
    }
  }, [user]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.cliente-search-container')) {
        setShowSearchCliente(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchClientes = async () => {
    if (!user?.id_empresa) return;
    
    setLoadingClientes(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get<Cliente[]>(
        `${process.env.REACT_APP_PADRON_URL}/leer_padron/${user.id_empresa}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setClientes(response.data);
    } catch (error: any) {
      console.error('Error al cargar clientes:', error);
    } finally {
      setLoadingClientes(false);
    }
  };

  const handleClienteSelect = (cliente: Cliente) => {
    setSelectedCliente(cliente);
    setSearchCliente(`${cliente.nc} - ${cliente.denominacion}`);
    setShowSearchCliente(false);
    // Buscar tickets pendientes para el cliente seleccionado
    fetchTicketsPendientes(cliente.id);
    // Obtener acumulado de ganancias para el cliente seleccionado
    fetchAcumuladoGanancias(cliente.id);
  };

  const handleClienteClear = () => {
    setSelectedCliente(null);
    setSearchCliente('');
    setShowSearchCliente(true);
    setTicketsPendientes([]);
    setAcumuladoGanancias(0);
  };

  const fetchTicketsPendientes = async (clienteId: number) => {
    if (!user?.id_empresa || !user?.id_sucursal) return;
    
    setLoadingTickets(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_LIQUIDACION_URL}/pendiente_liquidacion_compra/${user.id_empresa}/${user.id_sucursal}/${clienteId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.data.tickets_pendientes) {
        // Agregar campos calculados a cada ticket
        const ticketsConCalculos = response.data.tickets_pendientes.map((ticket: any) => ({
          ...ticket,
          precio_por_tonelada: 0, // Valor inicial, se puede editar
          subtotal: 0 // Se calculará automáticamente
        }));
        setTicketsPendientes(ticketsConCalculos);
        message.success(`Se encontraron ${response.data.total} tickets pendientes`);
      } else {
        setTicketsPendientes([]);
        message.info('No se encontraron tickets pendientes para este cliente');
      }
    } catch (error: any) {
      console.error('Error al cargar tickets pendientes:', error);
      message.error('Error al cargar los tickets pendientes');
      setTicketsPendientes([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  const fetchRetenciones = async () => {
    if (!user?.id_empresa) return;
    
    setLoadingRetenciones(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_LIQUIDACION_URL}/listar_retenciones/${user.id_empresa}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      const retencionesData = response.data.results || response.data;
      
      // Filtrar retenciones que NO tengan aplicar="SERVICIO"
      const retencionesFiltradas = retencionesData.filter((retencion: Retencion) => 
        retencion.aplicar !== "SERVICIO"
      ).map((retencion: Retencion) => ({
        ...retencion,
        seleccionada: true // Inicializar checkbox en true
      }));
      
      setRetenciones(retencionesFiltradas);
      message.success(`Se cargaron ${retencionesFiltradas.length} retenciones`);
    } catch (error: any) {
      console.error('Error al cargar retenciones:', error);
      message.error('Error al cargar las retenciones');
      setRetenciones([]);
    } finally {
      setLoadingRetenciones(false);
    }
  };

  const fetchAcumuladoGanancias = async (clienteId: number) => {
    if (!user?.id_empresa || !user?.id_sucursal) return;
    
    setLoadingAcumulado(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_LIQUIDACION_URL}/acumulado_ganancias/${user.id_empresa}/${user.id_sucursal}/${clienteId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.data.acumulado_subtotal !== undefined) {
        setAcumuladoGanancias(response.data.acumulado_subtotal);
        console.log('Acumulado de ganancias obtenido:', response.data);
      } else {
        setAcumuladoGanancias(0);
      }
    } catch (error: any) {
      console.error('Error al cargar acumulado de ganancias:', error);
      setAcumuladoGanancias(0);
    } finally {
      setLoadingAcumulado(false);
    }
  };

  // Función para calcular el importe de ganancias según la lógica compleja
  const calcularImporteGanancias = (retencion: Retencion): number => {
    const A1 = acumuladoGanancias; // Acumulado del mes
    const A2 = totalSubtotal; // Subtotal actual
    const A3 = retencion.acumulado_ganancia; // Acumulado_ganancias de la retención
    
    let IM = 0; // Importe para el cálculo % de ganancias
    
    if (A1 > A3) {
      // Si A1 > A3 -> IM = A2
      IM = A2;
    } else if ((A1 + A2) > A3) {
      // Si (A1 + A2) > A3 -> IM = A1 + A2 - A3
      IM = A1 + A2 - A3;
    } else {
      // Si no -> IM = 0
      IM = 0;
    }
    
    // Aplicar el porcentaje al importe calculado
    return (IM * retencion.porcentaje) / 100;
  };

  const handleRetencionesChange = (checked: boolean) => {
    setRetencionesActivas(checked);
    if (checked) {
      fetchRetenciones();
    } else {
      setRetenciones([]);
    }
  };

  // Cargar retenciones automáticamente al inicializar el componente
  useEffect(() => {
    if (retencionesActivas && user?.id_empresa) {
      fetchRetenciones();
    }
  }, [user?.id_empresa, retencionesActivas]);

  // Función para grabar la liquidación
  const handleGrabarTodo = async () => {
    setLoadingGrabar(true);
    try {
      if (!selectedCliente) {
        message.error('Debe seleccionar un cliente');
        return;
      }

      if (ticketsPendientes.length === 0) {
        message.error('No hay tickets para liquidar');
        return;
      }

      // Filtrar tickets que tienen precio (no se liquidan los que tienen $/tn = 0)
      const ticketsParaLiquidar = ticketsPendientes.filter(ticket => 
        ticket.precio_por_tonelada && ticket.precio_por_tonelada !== 0
      );

      if (ticketsParaLiquidar.length === 0) {
        message.error('No hay tickets con precio asignado para liquidar');
        return;
      }

      // Filtrar retenciones seleccionadas (no se incluyen las que tienen checkbox = false)
      const retencionesParaLiquidar = retenciones.filter(retencion => 
        retencion.seleccionada === true
      );

      // Preparar datos para enviar
      const liquidacionData = {
        id_sucursal: user?.id_sucursal,
        id_cliente: selectedCliente.id,
        tickets: ticketsParaLiquidar.map(ticket => ({
          id: ticket.id,
          numero_ticket: ticket.numero_ticket,
          kilos_netos: ticket.kilos_netos,
          precio_por_tonelada: ticket.precio_por_tonelada,
          subtotal: ticket.subtotal
        })),
        retenciones: retencionesParaLiquidar.map(retencion => {
          let importe = 0;
          if (retencion.seleccionada) {
            if (retencion.tipo === "RENTAS") {
              // Para RENTAS: solo TotalKilos * $/TN
              importe = (totalKilos * (retencion.portonelada || 0)) / 1000;
            } else if (retencion.tipo === "GANANCIAS" || retencion.tipo === "GANANCIA") {
              // Para GANANCIAS: usar la lógica compleja con acumulado_ganancia
              importe = calcularImporteGanancias(retencion);
            } else if (retencion.tipo === "IVA") {
              // Para IVA: solo TotalSubtotal * Porcentaje / 100
              importe = (totalSubtotal * retencion.porcentaje) / 100;
            } else {
              // Para otros tipos: (TotalSubtotal * Porcentaje + TotalKilos * $/tn) / 1000
              const porcentajeCalculo = (totalSubtotal * retencion.porcentaje) / 100;
              const kilosCalculo = totalKilos * (retencion.portonelada || 0);
              importe = (porcentajeCalculo + kilosCalculo) / 1000;
            }
          }
          return {
            id: retencion.id,
            portonelada: retencion.portonelada,
            porcentaje: retencion.porcentaje,
            importe,
            seleccionada: retencion.seleccionada
          };
        }),
        subtotal: totalSubtotal,
        iva: ivaImporte,
        total_retenciones: totalRetenciones,
        total_general: totalGeneral
      };

      console.log('Datos a enviar:', liquidacionData);

      // Obtener token de autenticación
      const token = await getAccessTokenSilently();
      
      // Enviar datos al backend
      const response = await axios.post(
        `${process.env.REACT_APP_LIQUIDACION_URL}/crear_liquidacion/${user?.id_empresa}/`,
        liquidacionData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        message.success(`Liquidación creada exitosamente. Comprobante: ${response.data.data.comprobante}`);
        
        // Limpiar formulario después de grabar exitosamente
        setSelectedCliente(null);
        setTicketsPendientes([]);
        setRetenciones([]);
        setIvaPorcentaje('10.5');
        setRetencionesActivas(true);
        
        // Opcional: navegar de vuelta al listado
        // navigate('/listar_compraalgodon');
      } else {
        message.error('Error al crear la liquidación');
      }

    } catch (error: any) {
      console.error('Error al grabar liquidación:', error);
      message.error('Error al grabar la liquidación');
    } finally {
      setLoadingGrabar(false);
    }
  };

  const handleRetencionSelect = (retencionId: number, checked: boolean) => {
    setRetenciones(prev => 
      prev.map(retencion => 
        retencion.id === retencionId 
          ? { ...retencion, seleccionada: checked }
          : retencion
      )
    );
  };

  const handlePrecioChange = (ticketId: number, precio: number) => {
    setTicketsPendientes(prev => 
      prev.map(ticket => {
        if (ticket.id === ticketId) {
          const subtotal = (ticket.kilos_netos / 1000) * precio; // Convertir kilos a toneladas
          return {
            ...ticket,
            precio_por_tonelada: precio,
            subtotal
          };
        }
        return ticket;
      })
    );
  };

  // Calcular totales
  const totalKilos = ticketsPendientes.reduce((sum, ticket) => {
    // Solo sumar kilos si el precio por tonelada es diferente de cero
    return (ticket.precio_por_tonelada && ticket.precio_por_tonelada !== 0) ? sum + ticket.kilos_netos : sum;
  }, 0);
  const totalSubtotal = ticketsPendientes.reduce((sum, ticket) => sum + (ticket.subtotal || 0), 0);
  const ivaImporte = (totalSubtotal * parseFloat(ivaPorcentaje)) / 100;

  // Calcular Total Retenciones
  const totalRetenciones = retenciones.reduce((total, retencion) => {
    if (retencion.seleccionada) {
      if (retencion.tipo === "RENTAS") {
        // Para RENTAS: solo TotalKilos * $/TN
        const importe = (totalKilos * (retencion.portonelada || 0)) / 1000;
        return total + importe;
      } else if (retencion.tipo === "GANANCIAS" || retencion.tipo === "GANANCIA") {
        // Para GANANCIAS: usar la lógica compleja con acumulado_ganancia
        const importe = calcularImporteGanancias(retencion);
        return total + importe;
      } else if (retencion.tipo === "IVA") {
        // Para IVA: solo TotalSubtotal * Porcentaje / 100
        const importe = (totalSubtotal * retencion.porcentaje) / 100;
        return total + importe;
      } else {
        // Para otros tipos: (TotalSubtotal * Porcentaje + TotalKilos * $/tn) / 1000
        const porcentajeCalculo = (totalSubtotal * retencion.porcentaje) / 100;
        const kilosCalculo = totalKilos * (retencion.portonelada || 0);
        const importe = (porcentajeCalculo + kilosCalculo) / 1000;
        return total + importe;
      }
    }
    return total;
  }, 0);

  // Calcular Total General: Total Subtotal + IVA - Retenciones
  const totalGeneral = totalSubtotal + ivaImporte - totalRetenciones;

  return (
    <div style={{ padding: '0' }}>
      <PageTitle style={{ marginBottom: '12px', fontSize: '24px' }}>Liquidación de Compra</PageTitle>

      <StyledCard title="Información de Liquidación" bodyStyle={{ padding: '12px' }}>
        <style>{`
          .liquidacion-compra-table .ant-table {
            font-size: 12px;
          }
          .liquidacion-compra-table .ant-table-thead > tr > th {
            font-size: 12px;
            padding: 6px 8px;
            font-weight: 600;
          }
          .liquidacion-compra-table .ant-table-tbody > tr > td {
            font-size: 12px;
            padding: 6px 8px;
          }
          .liquidacion-compra-table .ant-table-summary {
            font-size: 12px;
          }
        `}</style>
        {/* Búsqueda del Padrón */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '12px' }}>
          <div style={{ position: 'relative', flex: '1', maxWidth: '400px' }} className="cliente-search-container">
            <Input
              placeholder="Buscar cliente en el padrón..."
              style={{
                paddingLeft: '28px',
                paddingRight: selectedCliente ? '28px' : '10px',
                borderRadius: theme.borderRadius.md,
                backgroundColor: selectedCliente ? theme.colors.backgroundGray : 'white',
                cursor: 'pointer',
                width: '100%',
                fontSize: '13px',
                height: '32px'
              }}
              value={selectedCliente ? `${selectedCliente.nc} - ${selectedCliente.denominacion}` : searchCliente}
              onChange={e => {
                setSearchCliente(e.target.value);
                setSelectedCliente(null);
                setShowSearchCliente(true);
              }}
              onFocus={() => setShowSearchCliente(true)}
              disabled={loadingClientes}
            />
            {/* Lupa */}
            <span style={{
              position: 'absolute',
              left: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: theme.colors.textMuted,
              pointerEvents: 'none',
              fontSize: '14px'
            }}>🔍</span>
            {/* Limpiar selección */}
            {selectedCliente && (
              <span
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: theme.colors.textMuted,
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  zIndex: 1100
                }}
                onClick={handleClienteClear}
                title="Limpiar selección"
              >
                ×
              </span>
            )}
            {/* Dropdown */}
            {showSearchCliente && clientes.length > 0 && !selectedCliente && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.md,
                maxHeight: '180px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: theme.shadows.md
              }}>
                {clientes.filter(c => {
                  const search = searchCliente.trim().toLowerCase();
                  return (
                    c.nc.toLowerCase().includes(search) ||
                    c.denominacion.toLowerCase().includes(search)
                  );
                }).map(cliente => (
                  <div
                    key={cliente.id}
                    style={{
                      padding: '6px 10px',
                      cursor: 'pointer',
                      borderBottom: `1px solid ${theme.colors.borderLight}`,
                      fontSize: '13px'
                    }}
                    onMouseOver={e => (e.currentTarget.style.backgroundColor = theme.colors.backgroundGray)}
                    onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                    onClick={() => handleClienteSelect(cliente)}
                  >
                    {cliente.nc} - {cliente.denominacion}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Mostrar acumulado de ganancias en el margen derecho */}
          {selectedCliente && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              backgroundColor: '#e6f7ff',
              borderRadius: theme.borderRadius.md,
              border: `1px solid #91d5ff`,
              minWidth: '220px',
              justifyContent: 'flex-end',
              fontSize: '12px'
            }}>
              <span style={{ fontWeight: 600, color: theme.colors.primary }}>
                Acumulado del mes:
              </span>
              {loadingAcumulado ? (
                <span style={{ color: theme.colors.textMuted }}>Cargando...</span>
              ) : (
                <span style={{ 
                  fontWeight: 600, 
                  color: theme.colors.primary,
                  fontSize: '13px'
                }}>
                  {new Intl.NumberFormat('es-AR', {
                    style: 'currency',
                    currency: 'ARS',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(acumuladoGanancias)}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Tabla de Tickets Pendientes */}
        {selectedCliente && (
          <StyledCard 
            title="Tickets Pendientes de Liquidación"
            style={{ marginTop: '12px' }}
            bodyStyle={{ padding: '12px' }}
          >
            <Table
              className="liquidacion-compra-table"
              columns={[
                {
                  title: 'N° Ticket',
                  dataIndex: 'numero_ticket',
                  key: 'numero_ticket',
                  width: 100,
                },
                {
                  title: 'N° Remito',
                  dataIndex: 'numero_remito',
                  key: 'numero_remito',
                  width: 120,
                },
                {
                  title: 'DTV',
                  dataIndex: 'dtv',
                  key: 'dtv',
                  width: 120,
                },
                {
                  title: 'Kilos Netos',
                  dataIndex: 'kilos_netos',
                  key: 'kilos_netos',
                  width: 120,
                  align: 'right',
                  render: (value) => new Intl.NumberFormat('es-AR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(value),
                },
                {
                  title: 'Fecha',
                  dataIndex: 'fecha',
                  key: 'fecha',
                  width: 110,
                },
                {
                  title: '$/Tn',
                  dataIndex: 'precio_por_tonelada',
                  key: 'precio_por_tonelada',
                  width: 100,
                  align: 'right',
                  render: (value, record) => (
                    <Input
                      type="number"
                      step="0.01"
                      value={value || 0}
                      onChange={(e) => handlePrecioChange(record.id, parseFloat(e.target.value) || 0)}
                      style={{ 
                        textAlign: 'right', 
                        width: '100%',
                        fontSize: '12px',
                        height: '28px',
                        padding: '2px 8px'
                      }}
                      placeholder="0.00"
                    />
                  ),
                },
                {
                  title: 'Subtotal',
                  dataIndex: 'subtotal',
                  key: 'subtotal',
                  width: 120,
                  align: 'right',
                  render: (value) => new Intl.NumberFormat('es-AR', {
                    style: 'currency',
                    currency: 'ARS',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(value || 0),
                },
              ]}
              dataSource={ticketsPendientes}
              rowKey="id"
              loading={loadingTickets}
              pagination={false}
              scroll={{ y: 180 }}
              size="small"
              bordered
              style={{ fontSize: '12px' }}
              locale={{
                emptyText: 'No hay tickets pendientes para este cliente'
              }}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                    <Table.Summary.Cell index={0}></Table.Summary.Cell>
                    <Table.Summary.Cell index={1}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      <div style={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {new Intl.NumberFormat('es-AR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(totalKilos)}
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}></Table.Summary.Cell>
                    <Table.Summary.Cell index={5}></Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>
                      <div style={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {new Intl.NumberFormat('es-AR', {
                          style: 'currency',
                          currency: 'ARS',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(totalSubtotal)}
                      </div>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  <Table.Summary.Row style={{ backgroundColor: '#f9f9f9' }}>
                    <Table.Summary.Cell index={0}></Table.Summary.Cell>
                    <Table.Summary.Cell index={1}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}></Table.Summary.Cell>
                    <Table.Summary.Cell index={4}></Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                        <span style={{ fontWeight: 600 }}>IVA:</span>
                        <Select
                          value={ivaPorcentaje}
                          onChange={setIvaPorcentaje}
                          style={{ width: '70px', fontSize: '12px' }}
                          size="small"
                        >
                          <Option value="0.00">0.00</Option>
                          <Option value="10.5">10.5</Option>
                          <Option value="21">21</Option>
                        </Select>
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>
                      <div style={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {new Intl.NumberFormat('es-AR', {
                          style: 'currency',
                          currency: 'ARS',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(ivaImporte)}
                      </div>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  <Table.Summary.Row style={{ backgroundColor: '#f0f0f0' }}>
                    <Table.Summary.Cell index={0}></Table.Summary.Cell>
                    <Table.Summary.Cell index={1}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}></Table.Summary.Cell>
                    <Table.Summary.Cell index={4}></Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                        <Checkbox
                          checked={retencionesActivas}
                          onChange={(e) => handleRetencionesChange(e.target.checked)}
                          style={{ fontSize: '12px' }}
                        >
                          Retenciones
                        </Checkbox>
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>
                      {retencionesActivas && retenciones.length > 0 && (
                        <div style={{ textAlign: 'right', fontWeight: 600, color: theme.colors.primary, fontSize: '12px' }}>
                          {retenciones.length} retenciones
                        </div>
                      )}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </StyledCard>
        )}

        {/* Tabla de Retenciones */}
        {selectedCliente && retencionesActivas && retenciones.length > 0 && (
          <StyledCard 
            title="Retenciones Disponibles"
            style={{ marginTop: '12px' }}
            bodyStyle={{ padding: '12px' }}
          >
            <Table
              className="liquidacion-compra-table"
              columns={[
                {
                  title: '',
                  key: 'checkbox',
                  width: 50,
                  align: 'center',
                  render: (_, record) => (
                    <Checkbox
                      checked={record.seleccionada || false}
                      onChange={(e) => handleRetencionSelect(record.id, e.target.checked)}
                    />
                  ),
                },
                {
                  title: 'Descripción',
                  dataIndex: 'descripcion',
                  key: 'descripcion',
                  width: 200,
                },
                {
                  title: 'Tipo',
                  dataIndex: 'tipo',
                  key: 'tipo',
                  width: 100,
                },
                {
                  title: 'Provincia',
                  dataIndex: 'provincia_nombre',
                  key: 'provincia_nombre',
                  width: 150,
                },
                {
                  title: 'Porcentaje',
                  dataIndex: 'porcentaje',
                  key: 'porcentaje',
                  width: 100,
                  align: 'right',
                  render: (value) => `${value}%`,
                },
                {
                  title: '$/TN',
                  dataIndex: 'portonelada',
                  key: 'portonelada',
                  width: 120,
                  align: 'right',
                  render: (value) => {
                    console.log('Portonelada value:', value); // Debug
                    return new Intl.NumberFormat('es-AR', {
                      style: 'currency',
                      currency: 'ARS',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }).format(value || 0);
                  },
                },
                {
                  title: 'Importe',
                  key: 'importe',
                  width: 120,
                  align: 'right',
                  render: (_, record) => {
                    let importe = 0;
                    if (record.seleccionada) {
                      if (record.tipo === "RENTAS") {
                        // Para RENTAS: solo TotalKilos * $/TN
                        importe = (totalKilos * (record.portonelada || 0)) / 1000;
                      } else if (record.tipo === "GANANCIAS" || record.tipo === "GANANCIA") {
                        // Para GANANCIAS: usar la lógica compleja con acumulado_ganancia
                        importe = calcularImporteGanancias(record);
                      } else if (record.tipo === "IVA") {
                        // Para IVA: solo TotalSubtotal * Porcentaje / 100
                        importe = (totalSubtotal * record.porcentaje) / 100;
                      } else {
                        // Para otros tipos: (TotalSubtotal * Porcentaje + TotalKilos * $/tn) / 1000
                        const porcentajeCalculo = (totalSubtotal * record.porcentaje) / 100;
                        const kilosCalculo = totalKilos * (record.portonelada || 0);
                        importe = (porcentajeCalculo + kilosCalculo) / 1000;
                      }
                    }
                    return new Intl.NumberFormat('es-AR', {
                      style: 'currency',
                      currency: 'ARS',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }).format(importe);
                  },
                },
              ]}
              dataSource={retenciones}
              rowKey="id"
              loading={loadingRetenciones}
              pagination={false}
              scroll={{ y: 130 }}
              size="small"
              bordered
              style={{ fontSize: '12px' }}
              locale={{
                emptyText: 'No hay retenciones disponibles'
              }}
            />
            
            {/* Total Retenciones */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              marginTop: '10px',
              padding: '6px 12px',
              backgroundColor: theme.colors.backgroundGray,
              borderRadius: theme.borderRadius.md
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                fontSize: '13px',
                fontWeight: 600
              }}>
                <span>Total Retenciones:</span>
                <span style={{ 
                  color: theme.colors.primary,
                  minWidth: '110px',
                  textAlign: 'right'
                }}>
                  {new Intl.NumberFormat('es-AR', {
                    style: 'currency',
                    currency: 'ARS',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(totalRetenciones)}
                </span>
              </div>
            </div>
          </StyledCard>
        )}

        {/* Total General */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          marginTop: '12px',
          padding: '8px 12px',
          backgroundColor: '#e6f7ff',
          borderRadius: theme.borderRadius.md,
          border: `2px solid ${theme.colors.primary}`
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            fontSize: '14px',
            fontWeight: 600
          }}>
            <span>TOTAL GENERAL:</span>
            <span style={{ 
              color: theme.colors.primary,
              minWidth: '110px',
              textAlign: 'right'
            }}>
              {new Intl.NumberFormat('es-AR', {
                style: 'currency',
                currency: 'ARS',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(totalGeneral)}
            </span>
          </div>
        </div>

        {/* Botones de acción */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '8px',
          marginTop: '12px',
          padding: '8px 0'
        }}>
          {/* Botón Volver a Listado */}
          <Button 
            size="small"
            onClick={() => navigate('/listar_compraalgodon')}
            style={{
              minWidth: '120px',
              height: '28px',
              fontSize: '12px',
              color: theme.colors.textSecondary,
              borderColor: theme.colors.border
            }}
          >
            Volver a Listado
          </Button>

          {/* Botón GrabarTodo */}
          <Button 
            type="primary"
            size="small"
            loading={loadingGrabar}
            disabled={loadingGrabar}
            onClick={handleGrabarTodo}
            style={{
              minWidth: '120px',
              height: '28px',
              fontSize: '12px',
              backgroundColor: theme.colors.primary,
              borderColor: theme.colors.primary
            }}
          >
            GrabarTodo
          </Button>
        </div>
      </StyledCard>
    </div>
  );
};

export default LiquidacionCompra;
